import { Rectangle, Texture } from 'pixi.js'
import manifest from '../sprites/manifest.json'
import { LOAD, ON_SCREEN_R } from '../load.config'
import { textureSize, packShelves } from './atlasLayout'
import { laneRadius } from './corridors'

// Item B / F: every texture of the war scene, uploaded to the GPU at load.
//   main atlas(es)   cells (8 terrains), overlays, Broods, eggs, rockets,
//                    drones, stamps, missile, particle, 20 HUD icons
//   structure library  LOAD.structureLibraryStructures x LOAD.structureLibraryLevels
//                    distinct 256x256 textures, 64 per 2048 atlas (no padding)
//   background       one full-screen texture at native resolution
// For each key: the override PNG from src/gate1/sprites/ if one exists, else a
// generated placeholder; either way drawn at the item F size, so art never
// changes the graded load. A key with `frames` in the manifest is an animation:
// its PNG is a horizontal strip of square frames, each packed as its own
// texture (`<key>#<i>`) at the item F size; with no PNG it has no placeholder.

const pngs = (ctx) => {
    const out = new Map()
    for (const k of ctx.keys()) { const m = ctx(k); out.set(k.replace('./', ''), m.default ?? m) }
    return out
}
// Override PNGs present in src/gate1/sprites (webpack finds them at build time).
// Not `typeof require.context === 'function' ? … : …`: webpack folds that
// test to false, so no override ever loaded. Jest has no require.context, so
// the call throws there and the placeholders are used.
const contextPngs = load => { try { return pngs(load()) } catch { return new Map() } }
const OVERRIDE_URLS = contextPngs(() => require.context('../sprites', false, /\.png$/))
const LIBRARY_URLS = contextPngs(() => require.context('../sprites/structures', false, /\.png$/))

const LIB_TOTAL = LOAD.structureLibraryStructures * LOAD.structureLibraryLevels
const LIB_PER_ROW = LOAD.atlasMaxPx / LOAD.structureTexturePx
const LIB_PER_ATLAS = LIB_PER_ROW * LIB_PER_ROW
const libFile = e => `structure-${String(Math.floor(e / LOAD.structureLibraryLevels)).padStart(2, '0')}-l${(e % LOAD.structureLibraryLevels) + 1}.png`

export function overridesInUse() {
    const main = Object.entries(manifest.sprites).filter(([, m]) => OVERRIDE_URLS.has(m.file)).map(([key, m]) => ({ key, file: m.file }))
    const lib = [...LIBRARY_URLS.keys()].map(file => ({ key: `library:${file}`, file: `structures/${file}` }))
    return [...main, ...lib]
}

function loadImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error(`could not load ${url}`))
        img.src = url
    })
}

// ── Placeholder drawing (Canvas 2D) ──────────────────────────────────────────
function hexPath(ctx, w, h, inset = 0) {
    const cx = w / 2, cy = h / 2, rx = w / 2 - inset, ry = h / 2 - inset
    ctx.beginPath()
    ctx.moveTo(cx + rx, cy)
    ctx.lineTo(cx + rx / 2, cy - ry)
    ctx.lineTo(cx - rx / 2, cy - ry)
    ctx.lineTo(cx - rx, cy)
    ctx.lineTo(cx - rx / 2, cy + ry)
    ctx.lineTo(cx + rx / 2, cy + ry)
    ctx.closePath()
}
function shadedFill(ctx, w, h, color, edge) {
    const g = ctx.createLinearGradient(0, 0, 0, h)
    g.addColorStop(0, color)
    g.addColorStop(1, edge)
    ctx.fillStyle = g
    ctx.fill()
}
const TERRAIN = [
    ['#6a8c57', '#4d6a3e'], ['#73925a', '#55703f'], ['#5f8450', '#466237'], ['#7a9a62', '#5a7846'],
    ['#58708f', '#3f5470'], ['#617a99', '#465c79'], ['#4f6886', '#384d69'], ['#6a82a0', '#4e6584'],
]
function terrain(c, w, h, i) {
    hexPath(c, w, h, w * 0.03)
    shadedFill(c, w, h, TERRAIN[i][0], TERRAIN[i][1])
    // A little per-variant texture: dots in a fixed pattern.
    c.fillStyle = 'rgba(255,255,255,0.08)'
    for (let k = 0; k < 6 + i; k++) {
        const a = (k * 2.39996 + i) % (Math.PI * 2), r = (w * 0.3 * ((k * 37 + i * 11) % 10)) / 10
        c.beginPath(); c.arc(w / 2 + Math.cos(a) * r, h / 2 + Math.sin(a) * r * 0.85, w * 0.035, 0, Math.PI * 2); c.fill()
    }
}
const PLACEHOLDER = {
    scan: (c, w, h) => { hexPath(c, w, h, w * 0.03); c.fillStyle = 'rgba(255,255,255,0.30)'; c.fill(); c.lineWidth = w * 0.05; c.strokeStyle = 'rgba(200,160,255,0.9)'; c.stroke() },
    'scan-aged': (c, w, h) => { hexPath(c, w, h, w * 0.03); c.fillStyle = 'rgba(220,220,220,0.22)'; c.fill() },
    crater: (c, w, h) => { hexPath(c, w, h, w * 0.05); const g = c.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2); g.addColorStop(0, '#1a110c'); g.addColorStop(1, '#3a2618'); c.fillStyle = g; c.fill() },
    'crater-impact': (c, w, h) => { hexPath(c, w, h, w * 0.05); const g = c.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2); g.addColorStop(0, '#a83a14'); g.addColorStop(1, '#4a1a0c'); c.fillStyle = g; c.fill() },
    structure: (c, w, h) => { hexPath(c, w, h, w * 0.06); shadedFill(c, w, h, '#45d1bd', '#1e8a7c') },
    'structure-dead': (c, w, h) => { hexPath(c, w, h, w * 0.06); shadedFill(c, w, h, '#4a4a4a', '#2a2a2a'); c.lineWidth = w * 0.08; c.strokeStyle = '#c03030'; c.beginPath(); c.moveTo(w * 0.28, h * 0.28); c.lineTo(w * 0.72, h * 0.72); c.moveTo(w * 0.72, h * 0.28); c.lineTo(w * 0.28, h * 0.72); c.stroke() },
    'brood-nest': (c, w, h) => { hexPath(c, w, h, w * 0.05); shadedFill(c, w, h, '#f0b429', '#b07a10'); hexPath(c, w, h, w * 0.32); c.fillStyle = '#7a4f06'; c.fill() },
    'brood-drone': (c, w, h) => { hexPath(c, w, h, w * 0.05); shadedFill(c, w, h, '#f5d27a', '#c9a040'); c.lineWidth = w * 0.05; c.strokeStyle = '#7a4f06'; c.beginPath(); c.moveTo(w * 0.3, h / 2); c.lineTo(w * 0.7, h / 2); c.moveTo(w / 2, h * 0.3); c.lineTo(w / 2, h * 0.7); c.stroke() },
    'brood-building': (c, w, h) => { hexPath(c, w, h, w * 0.05); c.fillStyle = 'rgba(176,138,64,0.6)'; c.fill(); c.setLineDash([w * 0.08, w * 0.06]); c.lineWidth = w * 0.05; c.strokeStyle = '#f0d080'; c.stroke() },
    egg: (c, w, h) => { c.beginPath(); c.ellipse(w / 2, h / 2, w * 0.32, h * 0.42, 0, 0, Math.PI * 2); const g = c.createRadialGradient(w * 0.42, h * 0.38, 0, w / 2, h / 2, w * 0.45); g.addColorStop(0, '#fffbe8'); g.addColorStop(1, '#e8d79a'); c.fillStyle = g; c.fill() },
    rocket: (c, w, h) => { c.beginPath(); c.moveTo(w / 2, h * 0.08); c.lineTo(w * 0.68, h * 0.62); c.lineTo(w * 0.62, h * 0.9); c.lineTo(w * 0.38, h * 0.9); c.lineTo(w * 0.32, h * 0.62); c.closePath(); shadedFill(c, w, h, '#ff6a5a', '#b02a20') },
    'drone-vet': (c, w, h) => droneTile(c, w, h, '#d8dde4', '#7d8792'),
    'drone-moved': (c, w, h) => droneTile(c, w, h, '#9cc8f0', '#4a7fae'),
    'drone-green': (c, w, h) => droneTile(c, w, h, '#a6e3a1', '#4e9a48'),
    stamp: (c, w, h) => { c.beginPath(); c.roundRect(w * 0.04, h * 0.08, w * 0.92, h * 0.84, h * 0.25); c.fillStyle = '#7a2a12'; c.fill() },
    missile: (c, w, h) => { c.beginPath(); c.moveTo(w / 2, h * 0.04); c.lineTo(w * 0.62, h * 0.3); c.lineTo(w * 0.6, h * 0.8); c.lineTo(w * 0.72, h * 0.96); c.lineTo(w * 0.28, h * 0.96); c.lineTo(w * 0.4, h * 0.8); c.lineTo(w * 0.38, h * 0.3); c.closePath(); shadedFill(c, w, h, '#ffffff', '#9aa4ae') },
    particle: (c, w, h) => { const g = c.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2); g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.4, 'rgba(255,210,140,0.8)'); g.addColorStop(1, 'rgba(255,120,40,0)'); c.fillStyle = g; c.fillRect(0, 0, w, h) },
}
function droneTile(c, w, h, light, dark) {
    c.beginPath(); c.arc(w / 2, h / 2, w * 0.46, 0, Math.PI * 2)
    shadedFill(c, w, h, light, dark)
    c.lineWidth = w * 0.04; c.strokeStyle = '#20262d'; c.stroke()
    // Rotors.
    c.strokeStyle = '#20262d'; c.lineWidth = w * 0.05
    c.beginPath(); c.moveTo(w * 0.28, h * 0.28); c.lineTo(w * 0.72, h * 0.72); c.moveTo(w * 0.72, h * 0.28); c.lineTo(w * 0.28, h * 0.72); c.stroke()
    for (const [x, y] of [[0.28, 0.28], [0.72, 0.28], [0.28, 0.72], [0.72, 0.72]]) { c.beginPath(); c.arc(w * x, h * y, w * 0.09, 0, Math.PI * 2); c.stroke() }
    c.beginPath(); c.arc(w / 2, h / 2, w * 0.1, 0, Math.PI * 2); c.fillStyle = '#20262d'; c.fill()
}
function laneCapsule(c, w, h) {
    c.beginPath(); c.roundRect(w * 0.04, w * 0.04, w * 0.92, h - w * 0.08, w * 0.46)
    c.fillStyle = 'rgba(10,14,20,0.28)'; c.fill()
    c.lineWidth = w * 0.05; c.strokeStyle = 'rgba(230,236,245,0.55)'; c.stroke()
}

function icon(c, w, h, i) {
    const hue = (i * 47) % 360
    c.beginPath(); c.arc(w / 2, h / 2, w * 0.44, 0, Math.PI * 2)
    shadedFill(c, w, h, `hsl(${hue},55%,55%)`, `hsl(${hue},55%,32%)`)
    c.strokeStyle = 'rgba(255,255,255,0.85)'; c.lineWidth = w * 0.07; c.fillStyle = 'rgba(255,255,255,0.9)'
    const g = i % 5
    c.beginPath()
    if (g === 0) { c.moveTo(w * 0.3, h / 2); c.lineTo(w * 0.7, h / 2); c.moveTo(w / 2, h * 0.3); c.lineTo(w / 2, h * 0.7); c.stroke() }
    else if (g === 1) { c.arc(w / 2, h / 2, w * 0.18, 0, Math.PI * 2); c.stroke() }
    else if (g === 2) { c.moveTo(w * 0.35, h * 0.3); c.lineTo(w * 0.7, h / 2); c.lineTo(w * 0.35, h * 0.7); c.closePath(); c.fill() }
    else if (g === 3) { c.rect(w * 0.33, h * 0.33, w * 0.34, h * 0.34); c.fill() }
    else { c.moveTo(w * 0.3, h * 0.65); c.lineTo(w * 0.45, h * 0.35); c.lineTo(w * 0.55, h * 0.55); c.lineTo(w * 0.7, h * 0.3); c.stroke() }
}
const SHAPES = 5
function libraryStructure(c, w, h, e) {
    const n = Math.floor(e / LOAD.structureLibraryLevels), level = e % LOAD.structureLibraryLevels
    const hue = (n * 137.508) % 360
    const shape = n % SHAPES
    c.beginPath()
    if (shape === 0) hexPath(c, w, h, w * 0.06)
    else if (shape === 1) c.arc(w / 2, h / 2, w * 0.44, 0, Math.PI * 2)
    else if (shape === 2) c.roundRect(w * 0.1, h * 0.1, w * 0.8, h * 0.8, w * 0.12)
    else if (shape === 3) { c.moveTo(w / 2, h * 0.04); c.lineTo(w * 0.96, h / 2); c.lineTo(w / 2, h * 0.96); c.lineTo(w * 0.04, h / 2); c.closePath() }
    else { for (let k = 0; k < 10; k++) { const a = -Math.PI / 2 + (k * Math.PI) / 5, r = k % 2 ? w * 0.22 : w * 0.46; c.lineTo(w / 2 + Math.cos(a) * r, h / 2 + Math.sin(a) * r) } c.closePath() }
    shadedFill(c, w, h, `hsl(${hue},60%,${58 - level * 6}%)`, `hsl(${hue},65%,${30 - level * 4}%)`)
    c.lineWidth = w * 0.035; c.strokeStyle = `hsl(${hue},40%,18%)`; c.stroke()
    // Emblem: Math.floor(n / SHAPES) rings; milestone level as pips.
    c.strokeStyle = 'rgba(255,255,255,0.55)'; c.lineWidth = w * 0.025
    for (let r = 0; r <= Math.floor(n / SHAPES) % 4; r++) { c.beginPath(); c.arc(w / 2, h / 2, w * (0.08 + r * 0.06), 0, Math.PI * 2); c.stroke() }
    c.fillStyle = '#fff6c8'
    for (let p = 0; p <= level; p++) { c.beginPath(); c.arc(w * (0.5 + (p - level / 2) * 0.13), h * 0.8, w * 0.045, 0, Math.PI * 2); c.fill() }
}
function background(c, w, h) {
    const g = c.createLinearGradient(0, 0, 0, h)
    g.addColorStop(0, '#0e1218'); g.addColorStop(0.5, '#151b23'); g.addColorStop(1, '#0e1218')
    c.fillStyle = g; c.fillRect(0, 0, w, h)
    // Fine speckle so the texture is real content, not a flat fill.
    let seed = 7
    const rnd = () => ((seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff)
    for (let k = 0; k < (w * h) / 900; k++) { c.fillStyle = `rgba(255,255,255,${0.02 + rnd() * 0.05})`; c.fillRect(rnd() * w, rnd() * h, 1 + rnd() * 2, 1 + rnd() * 2) }
}

function drawStampText(c, w, h, text, dead) {
    c.fillStyle = dead ? 'rgba(255,255,255,0.45)' : '#ffffff'
    c.font = `700 ${Math.round(h * 0.62)}px Menlo, monospace`
    c.textAlign = 'center'
    c.textBaseline = 'middle'
    c.fillText(text, w / 2, h / 2 + h * 0.03)
}

const canvasOf = (w, h) => { const cv = document.createElement('canvas'); cv.width = w; cv.height = h; return cv }

// Once a canvas-backed texture is on the GPU, shrink its canvas to 0x0 so
// WebKit frees the backing store now, not at the next JS garbage collection
// (on iOS, three scenes' worth of stale atlas canvases pushed the WebKit GPU
// process past its 300 MB limit). Safe because PixiJS 8 never unloads these
// sources from the GPU (autoGarbageCollect defaults to false); only a lost
// WebGL context would need the canvas again.
export function releaseCanvases(sources) {
    for (const s of sources) {
        const cv = s?.source?.resource ?? s?.resource
        if (typeof HTMLCanvasElement !== 'undefined' && cv instanceof HTMLCanvasElement) { cv.width = 0; cv.height = 0 }
    }
}

// names: every texture name the scene needs (model `tex` values).
// screen: { width, height } in CSS px (the background is drawn at native px).
export async function buildAtlas({ names, R, resolution, screen, renderer = null }) {
    const images = new Map()
    await Promise.all(overridesInUse().map(async ({ key, file }) => {
        const url = key.startsWith('library:') ? LIBRARY_URLS.get(file.replace('structures/', '')) : OVERRIDE_URLS.get(file)
        images.set(key, await loadImage(url))
    }))
    const textures = new Map()
    const bases = []
    let bytes = 0

    // Main atlas: one entry per non-library texture name.
    const mainNames = [...new Set([...Object.keys(manifest.sprites).filter(k => k !== 'background' && k !== 'lane'), ...names.filter(n => !n.startsWith('lib:'))])]
    const animated = new Map(mainNames.filter(k => manifest.sprites[k]?.frames > 1).map(k => [k, images.has(k) ? manifest.sprites[k].frames : 0]))
    const entries = mainNames.flatMap(name => {
        if (animated.has(name)) {
            const kind = manifest.sprites[name].kind
            return Array.from({ length: animated.get(name) }, (_, frame) => ({ name: `${name}#${frame}`, key: name, kind, frame, frames: animated.get(name), px: textureSize(kind, R, resolution), onScreen: { w: ON_SCREEN_R[kind].w * R, h: ON_SCREEN_R[kind].h * R }, stampText: null, dead: false }))
        }
        const lane = name.match(/^lane-(\d+)$/)
        if (lane) {
            // Capacity lane: a capsule round `cap` slots, 2x its on-screen size.
            const cap = Number(lane[1]), H = Math.sqrt(3) * R, r = laneRadius(R)
            const onScreen = { w: 2 * r, h: (cap - 1) * 3 * H + 2 * r }
            const px = { w: Math.ceil(onScreen.w * resolution * LOAD.textureScale), h: Math.ceil(onScreen.h * resolution * LOAD.textureScale) }
            return { name, key: 'lane', kind: 'lane', px, onScreen, stampText: null, dead: false }
        }
        const stamp = name.match(/^stamp(-dead)?:(.*)$/)
        const key = stamp ? 'stamp' : name
        const kind = manifest.sprites[key]?.kind ?? 'cell'
        return { name, key, kind, px: textureSize(kind, R, resolution), onScreen: { w: ON_SCREEN_R[kind].w * R, h: ON_SCREEN_R[kind].h * R }, stampText: stamp?.[2] ?? null, dead: !!stamp?.[1] }
    })
    const { atlases, slots } = packShelves(entries.map(e => ({ id: e.name, w: e.px.w, h: e.px.h })), LOAD.atlasMaxPx)
    const mainCanvases = atlases.map(() => canvasOf(LOAD.atlasMaxPx, LOAD.atlasMaxPx))
    for (const e of entries) {
        const slot = slots.get(e.name)
        const c = mainCanvases[slot.atlas].getContext('2d')
        c.save(); c.translate(slot.x, slot.y); c.beginPath(); c.rect(0, 0, e.px.w, e.px.h); c.clip()
        const img = images.get(e.key)
        if (img && e.frames) { const fw = img.width / e.frames; c.drawImage(img, e.frame * fw, 0, fw, img.height, 0, 0, e.px.w, e.px.h) } // one frame of the strip, resized to spec
        else if (img) c.drawImage(img, 0, 0, e.px.w, e.px.h) // resized to spec
        else if (e.key.startsWith('terrain-')) terrain(c, e.px.w, e.px.h, Number(e.key.slice(8)))
        else if (e.key.startsWith('icon-')) icon(c, e.px.w, e.px.h, Number(e.key.slice(5)))
        else if (e.key === 'lane') laneCapsule(c, e.px.w, e.px.h)
        else PLACEHOLDER[e.key]?.(c, e.px.w, e.px.h)
        if (e.stampText != null) drawStampText(c, e.px.w, e.px.h, e.stampText, e.dead)
        c.restore()
    }
    const mainBases = mainCanvases.map(cv => Texture.from(cv))
    bases.push(...mainBases)
    for (const e of entries) {
        const slot = slots.get(e.name)
        const t = new Texture({ source: mainBases[slot.atlas].source, frame: new Rectangle(slot.x, slot.y, e.px.w, e.px.h) })
        t.gate1Size = e.onScreen
        textures.set(e.name, t)
    }
    const animations = new Map([...animated].filter(([, n]) => n > 0).map(([k, n]) => [k, Object.freeze(Array.from({ length: n }, (_, i) => textures.get(`${k}#${i}`)))]))
    bytes += mainCanvases.length * LOAD.atlasMaxPx * LOAD.atlasMaxPx * 4

    // Structure library: a fixed grid, 64 per atlas, so entry e is in atlas floor(e / 64).
    const libAtlases = Math.ceil(LIB_TOTAL / LIB_PER_ATLAS)
    const libCanvases = Array.from({ length: libAtlases }, () => canvasOf(LOAD.atlasMaxPx, LOAD.atlasMaxPx))
    const S = LOAD.structureTexturePx
    const fallback = images.get('structure')
    for (let e = 0; e < LIB_TOTAL; e++) {
        const a = Math.floor(e / LIB_PER_ATLAS), i = e % LIB_PER_ATLAS
        const x = (i % LIB_PER_ROW) * S, y = Math.floor(i / LIB_PER_ROW) * S
        const c = libCanvases[a].getContext('2d')
        c.save(); c.translate(x, y); c.beginPath(); c.rect(0, 0, S, S); c.clip()
        const img = images.get(`library:${libFile(e)}`) ?? fallback
        if (img) c.drawImage(img, 0, 0, S, S)
        else libraryStructure(c, S, S, e)
        c.restore()
    }
    const libBases = libCanvases.map(cv => Texture.from(cv))
    bases.push(...libBases)
    const structOnScreen = { w: ON_SCREEN_R.structure.w * R, h: ON_SCREEN_R.structure.h * R }
    for (let e = 0; e < LIB_TOTAL; e++) {
        const a = Math.floor(e / LIB_PER_ATLAS), i = e % LIB_PER_ATLAS
        const t = new Texture({ source: libBases[a].source, frame: new Rectangle((i % LIB_PER_ROW) * S, Math.floor(i / LIB_PER_ROW) * S, S, S) })
        t.gate1Size = structOnScreen
        textures.set(`lib:${e}`, t)
    }
    bytes += libAtlases * LOAD.atlasMaxPx * LOAD.atlasMaxPx * 4

    // Background: full screen, native pixels.
    const bw = Math.round(screen.width * resolution), bh = Math.round(screen.height * resolution)
    const bgCanvas = canvasOf(bw, bh)
    const bimg = images.get('background')
    if (bimg) bgCanvas.getContext('2d').drawImage(bimg, 0, 0, bw, bh)
    else background(bgCanvas.getContext('2d'), bw, bh)
    const bg = Texture.from(bgCanvas)
    bg.gate1Size = { w: screen.width, h: screen.height }
    bases.push(bg)
    textures.set('background', bg)
    bytes += bw * bh * 4

    // All loaded: upload every atlas to the GPU now, not on first draw.
    if (renderer?.texture?.initSource) {
        for (const b of bases) renderer.texture.initSource(b.source)
        releaseCanvases(bases)
    }

    return {
        get(name) {
            const t = textures.get(name)
            if (!t) throw new Error(`no texture "${name}" in the atlas`)
            return t
        },
        // An animation's frame textures in order, or null when its PNG is absent.
        frames(name) { return animations.get(name) ?? null },
        stats: {
            mainAtlases: mainCanvases.length,
            libraryAtlases: libAtlases,
            atlases: mainCanvases.length + libAtlases,
            atlasPx: LOAD.atlasMaxPx,
            libraryTextures: LIB_TOTAL,
            mainTextures: entries.length,
            background: `${bw}x${bh}`,
            textureBytes: bytes,
            overrides: overridesInUse().map(o => o.key),
            sizes: Object.fromEntries(entries.filter(e => manifest.sprites[e.name] || e.frame === 0).map(e => [e.frames ? `${e.key} (${e.frames} frames)` : e.name, `${e.px.w}x${e.px.h}`])),
        },
        destroy() {
            for (const t of textures.values()) if (!bases.includes(t)) t.destroy(false)
            for (const b of bases) b.destroy(true)
        },
    }
}
